import { useState, useEffect, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import {
  CardInfo,
  OperatorInfo,
  getAvailableCardInfos,
  getLayoutEntry,
  getAreaEntry,
  deleteCardUser,
  deleteCardImage,
  getCardPdf,
  createNewCard,
  AreaInfo,
  LayoutInfo,
  OperatorRole,
  getAllAreaEntries,
  getAllLayoutEntries,
  uploadImage,
  updateCardInfo,
} from "../Utils/BackendInterface";
import {
  SortInfo,
  sortSubject,
  SortType,
  listSort,
  getNFCLink,
  showPromiseNotification,
  setRowCheckboxState,
  showInfoNotification,
  showErrorNotification,
  getFileTypeFromName,
  showSuccessNotification,
  copyToClipboard,
  getSelectedUsers,
} from "../Utils/utils";
import { Card } from "./Card";
import { GeneralManagementList } from "./GeneralManagementList";
import closeIcon from "../resources/img/closeIcon.svg";
import trashIcon from "../resources/img/trashIcon.png";
import uploadIcon from "../resources/img/uploadIcon.svg";
import { Subject } from "rxjs";

export const UserManagementView = () => {
  const [t, i18n] = useTranslation();
  const [gettingData, setGettingData] = useState(false);
  const [showEditUserBox, setShowEditUserBox] = useState<CardInfo | undefined>();
  const [showNewUserBox, setShowNewUserBox] = useState(false);
  const [showImportCsvBox, setShowImportCsvBox] = useState(false);
  const [avaialbleCards, setAvailableCards] = useState<CardInfo[] | undefined>();
  const [searchFilter, setSearchFilter] = useState("");
  const [busyPdf, setBusyPdf] = useState(false);
  const [currentSorting, setCurrentSorting] = useState<SortInfo | undefined>();
  const [csvImportInfo, setCsvImportInfo] = useState<
    { idArea: number; idLayout: number } | undefined
  >();
  const [showDeleteOverlay, setShowDeleteOverlay] = useState(false);

  const outletInfo = useOutletContext<{ currentUser: OperatorInfo }>();

  const [downloaderInfo, setDownloaderInfo] = useState<
    { href: string; name: string } | undefined
  >();

  useEffect(() => {
    const sortSub = sortSubject.subscribe((sortInfo) => {
      setCurrentSorting(sortInfo);
    });

    return function cleanup() {
      sortSub?.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (currentSorting && currentSorting.type !== SortType.NONE) {
      const toBeSorted = avaialbleCards?.slice();
      toBeSorted?.sort((a: CardInfo, b: CardInfo) => {
        return listSort(a, b, currentSorting);
      });
      setAvailableCards(toBeSorted);
    } else if (currentSorting && currentSorting.type === SortType.NONE) {
      getData();
    }
  }, [currentSorting]);

  useEffect(() => {
    console.log("showedituserbox", showEditUserBox);
    if (!showEditUserBox) {
      console.log("called get data showedituserbox", showEditUserBox);
      getData();
    }
  }, [showEditUserBox]);

  useEffect(() => {
    console.log("shownewuserbox", showNewUserBox);
    if (!showNewUserBox) {
      console.log("called get data shownewuserbox", showNewUserBox);
      getData();
    }
  }, [showNewUserBox]);

  useEffect(() => {
    if (downloaderInfo) {
      document.getElementById("theDownloader")?.click();
    }
  }, [downloaderInfo]);

  const getData = () => {
    if (gettingData) {
      console.log("already getting data");
      return;
    }
    setGettingData(true);
    // setAvailableCards(undefined); // not sure if this should be enabled
    getAvailableCardInfos().then(async (infos) => {
      if (infos) {
        const displayCardInfos: CardInfo[] = [];

        // manual caching for speedup
        let _layoutDisplayNameCache: Map<number, string> = new Map();
        let _areaDisplayNameCache: Map<number, string> = new Map();
        for (const entry of infos) {
          // const theImgLink = await getImageLink(entry.nfcId);
          const currInfo = entry as any as CardInfo;
          if (!_layoutDisplayNameCache.has(entry.idLayout)) {
            _layoutDisplayNameCache.set(
              entry.idLayout,
              (await getLayoutEntry(entry.idLayout))?.name ?? ""
            );
          }
          if (!_areaDisplayNameCache.has(entry.idArea)) {
            _areaDisplayNameCache.set(entry.idArea, (await getAreaEntry(entry.idArea))?.name ?? "");
          }

          currInfo.layoutDisplayName = _layoutDisplayNameCache.get(entry.idLayout);
          currInfo.areaDisplayName = _areaDisplayNameCache.get(entry.idArea);
          currInfo.nfcLink = getNFCLink(entry.nfcId); // this isn't saved on the server, need to construct here
          // currInfo.picture = theImgLink;
          displayCardInfos.push(currInfo);
        }
        setAvailableCards(displayCardInfos);
      }

      setGettingData(false);
    });
  };

  const handleMenuDelete = async () => {
    const selectedUsers = getSelectedUsers();
    let notificationSubject: Subject<boolean>;
    if (selectedUsers.length) {
      notificationSubject = showPromiseNotification(
        t("info-started-deleting"),
        t("success-deleted-entries-title"),
        t("error-deleted-entries")
      );
    } else {
      return;
    }

    let totalSuccess = true;
    for (const user of selectedUsers) {
      // delete user
      const deleteUserRes = await deleteCardUser(user);

      totalSuccess &&= deleteUserRes;
    }
    if (!totalSuccess) {
      notificationSubject.next(false);
    } else {
      notificationSubject.next(true);
      setRowCheckboxState(false);
    }
    getData();
  };

  const handleMenuDownloadPdf = async () => {
    if (busyPdf) {
      console.warn("busy pdf download");
      showInfoNotification(t("info-pdf-download-progress"));
      return;
    }
    const selectedUsers = getSelectedUsers();
    console.log("selectedUsers", selectedUsers);
    if (!selectedUsers.length) {
      return;
    }

    setBusyPdf(true);
    // prepare notification
    const notificationSubject = showPromiseNotification(
      t("info-started-download"),
      t("success-finished-download"),
      t("error-finished-download")
    );

    for (const [i, user] of selectedUsers.entries()) {
      const theCard = avaialbleCards?.find((card) => card.nfcId === user);
      if (!theCard) {
        continue;
      }
      const layoutInfo = await getLayoutEntry(theCard.idLayout ?? 0);
      if (!layoutInfo) {
        notificationSubject.next(false);
        return;
      }
      const pdfData = await getCardPdf(
        user,
        // extraDetails.cardLabel,
        // extraDetails.cardNotice
        layoutInfo.label,
        layoutInfo.notice
      );
      if (!pdfData) {
        notificationSubject.next(false);
        setBusyPdf(false);
        return;
      }

      const label0 = theCard?.firstName ?? `card_${i}`;
      const label1 = theCard?.surname ?? "";
      setDownloaderInfo({
        name: `card_${label0}_${label1}`,
        href: window.URL.createObjectURL(pdfData),
      });
    }

    notificationSubject.next(true);
    setBusyPdf(false);
    console.log("should've completed the download", notificationSubject);
  };

  /**
   * Function for importing a csv file with JURA_HQ member details
   */
  const handleCsvInputLoaded = async (
    ev: ChangeEvent<HTMLInputElement>,
    idArea?: number,
    idLayout?: number
  ) => {
    if (
      !ev.target.files ||
      !ev.target.files.length ||
      idArea === undefined ||
      idLayout === undefined
    ) {
      showErrorNotification(t("error-csv-file"));
      return;
    }
    const fr = new FileReader();
    fr.onload = async () => {
      const result = fr.result as string;
      console.log("result input:", result);

      const newUserEntries = result.split("\n").map((e) => e.trim());
      if (!newUserEntries.length) return;

      const notificationSubject = showPromiseNotification(
        t("info-started-card-creation"),
        t("info-done-card-creation"),
        ""
      );
      // showInfoNotification(t("info-started-card-creation"));

      // get the details for creating new cards
      for (const [i, entry] of newUserEntries.entries()) {
        if (entry.includes("<") || entry.includes(">")) {
          console.info("skipping csv line that seems to be the header");
          continue;
        }
        const userInfos = entry.split(";").map((e) => e.trim());
        if (userInfos.length !== 7) {
          console.warn(`line ${i} of csv has missing input data, skipping line...`);
          continue;
        }
        let j = 0;
        const cardInfo: CardInfo = {
          surname: userInfos[j++].trim(),
          firstName: userInfos[j++].trim(),
          jobDescription: userInfos[j++].trim(),
          employeeNumber: userInfos[j++],
          email: userInfos[j++].trim(),
          telephone: userInfos[j++].trim(),
          mobile: userInfos[j++].trim(),
          idLayout: idLayout,
          website: "jura.com",
          idArea: idArea,
          nfcId: "", // this gets generated on the backend
        };
        const succAdd = await createNewCard(cardInfo);
        if (!succAdd) {
          console.warn("couldn't successfully add user from csv at line", i);
          showErrorNotification(
            "error-csv-card-creation" + ": " + cardInfo.firstName + " " + cardInfo.surname
          );
        }
      }
      // showInfoNotification(t("info-done-card-creation"));
      notificationSubject.next(true);

      getData(); // get fresh data

      // clear the input data
      (document.getElementById("theJURA_CsvSelector") as HTMLInputElement).value = "";
      setShowImportCsvBox(false);
    };
    fr.readAsText(ev.target.files[0]);
  };

  return (
    <div
      id="userManagementViewBody"
      className={`relative w-full h-full pb-2 flex flex-col justify-start items-center`}
    >
      <div id="userManagementMenuBar" className="w-11/12 mt-5 flex flex-row justify-between">
        <div
          id="userManagementLeftMenuPanel"
          className="w-fit flex flex-row justify-start gap-5 flex-wrap"
        >
          <div
            className="cursor-pointer h-8 pl-4 pr-4 text-jura-grey-font bg-jura-grey-button rounded-md flex flex-col justify-center"
            onClick={() => {
              setShowImportCsvBox(!showImportCsvBox);
            }}
          >
            {t("import-csv")}
          </div>

          <button
            id="exportButton"
            className="bg-jura-grey-button h-8 pl-4 pr-4 rounded-md text-jura-grey-font"
            onClick={() => {
              handleMenuDownloadPdf();
            }}
          >
            {t("export-card-design")}
          </button>
        </div>

        <div
          id="userManagementRightMenuPanel"
          className="ml-4 w-fit flex flex-row justify-center gap-6"
        >
          <input
            type="text"
            className="rounded-md pl-2 pr-2 h-8"
            placeholder={t("search-subject")}
            onChange={(e) => {
              setSearchFilter(e.target.value.toString());
            }}
          ></input>
          <button className="bg-jura-grey-button h-8 pl-4 pr-4 rounded-md text-jura-grey-font">
            {t("search")}
          </button>
        </div>
      </div>
      <div
        id="userManagementListContainer"
        className="w-11/12 h-fit min-h-70% mt-7 bg-white flex flex-col justify-start items-center overflow-x-auto"
      >
        {avaialbleCards !== undefined && (
          <GeneralManagementList
            setEditRowView={async (userInfo) => {
              setShowEditUserBox(userInfo as CardInfo);
            }}
            columns={[
              { labelKey: "nfcLink" },
              { labelKey: "surname", hasSorting: true },
              { labelKey: "firstName", hasSorting: true },
              // { labelKey: "picture" },
              { labelKey: "jobDescription" },
              { labelKey: "employeeNumber" },
              { labelKey: "email" },
              { labelKey: "telephone" },
              // { labelKey: "website" },
              { labelKey: "idLayout" },
              { labelKey: "idArea" },
            ]}
            rowValues={avaialbleCards.filter((card) => {
              // ƒilter for the search bar to work
              return (
                card.firstName.toLowerCase().includes(searchFilter?.toLowerCase()) ||
                card.surname.toLowerCase().includes(searchFilter?.toLowerCase()) ||
                card.jobDescription.toLowerCase().includes(searchFilter?.toLowerCase()) ||
                card.employeeNumber.toString().includes(searchFilter) ||
                card.areaDisplayName?.toLowerCase().includes(searchFilter?.toLowerCase()) ||
                card.layoutDisplayName?.toLowerCase().includes(searchFilter?.toLowerCase())
              );
            })}
            setAddRowView={setShowNewUserBox}
            hasTitleSelectCheckBox={true}
            hasRowSelectCheckBoxes={true}
            selectBoxIdKey="nfcId"
          />
        )}
      </div>
      <div id="userManagementMenuBar" className="w-11/12 mt-5 flex flex-row justify-between">
        <img
          id="deleteButton"
          className="bg-jura-grey-button h-9 p-1 rounded-md text-jura-grey-font cursor-pointer"
          onClick={() => {
            if (getSelectedUsers().length) {
              setShowDeleteOverlay(true);
            }
          }}
          src={trashIcon}
        />
      </div>

      {showEditUserBox !== undefined && (
        <EditUserView
          setEditUserView={(cardInfo) => setShowEditUserBox(cardInfo as CardInfo)}
          selectedCard={showEditUserBox}
        />
      )}

      {showNewUserBox && (
        <EditUserView
          setEditUserView={(show) => {
            setShowNewUserBox(!show ? false : true);
          }}
          selectedCard={undefined}
        />
      )}

      {showImportCsvBox && (
        <ImportCsvView
          hideView={() => setShowImportCsvBox(false)}
          setCsvInfo={(info) => setCsvImportInfo(info)}
        />
      )}

      {showDeleteOverlay && (
        <DeleteUserView
          handleDelete={() => {
            handleMenuDelete();
          }}
          hideView={() => {
            setShowDeleteOverlay(false);
          }}
        />
      )}

      <a
        className="hidden"
        id="theDownloader"
        href={downloaderInfo?.href}
        download={downloaderInfo?.name}
      />

      <input
        id="theJURA_CsvSelector"
        type="file"
        className="hidden"
        onChange={(e) => {
          handleCsvInputLoaded(e, csvImportInfo?.idArea, csvImportInfo?.idLayout);
        }}
        accept=".csv"
      />
    </div>
  );
};

export const ImportCsvView = (props: {
  hideView: () => void;
  setCsvInfo: (info: { idArea: number; idLayout: number }) => void;
}) => {
  const [t, i18n] = useTranslation();
  const outletInfo = useOutletContext<{ currentUser: OperatorInfo }>();
  const [availableAreas, setAvailableAreas] = useState<AreaInfo[]>([]);
  const [availableLayouts, setAvailableLayouts] = useState<LayoutInfo[]>([]);
  const [inputIdArea, setInputIdArea] = useState(outletInfo.currentUser.idArea);
  const [inputIdLayout, setInputIdLayout] = useState<number | undefined>();

  useEffect(() => {
    if (outletInfo.currentUser.role === OperatorRole.ADMIN) {
      getAllAreaEntries().then((res) => {
        if (res) setAvailableAreas(res);
      });
    } else {
      getAreaEntry(outletInfo.currentUser.idArea).then((res) => {
        if (res) setAvailableAreas([res]);
      });
    }
  }, []);

  useEffect(() => {
    if (inputIdArea !== undefined) {
      setAvailableLayouts([]); // need to reset when changing tharea
      setInputIdLayout(undefined);
      getAllLayoutEntries(inputIdArea).then((res) => {
        setAvailableLayouts(res);
        if (res.length > 0) setInputIdLayout(res[0].idLayout);
      });
    }
  }, [inputIdArea]);

  useEffect(() => {
    if (inputIdLayout !== undefined) {
      props.setCsvInfo({ idArea: inputIdArea, idLayout: inputIdLayout });
    }
  }, [inputIdLayout, inputIdArea]);

  return (
    // the enclosing black bachground
    <div
      className={`absolute w-full h-full top-0 left-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      {/* the actual container for the content */}
      <div className={`w-30em h-fit bg-jura-beige flex flex-col justify-start items-center`}>
        <div className="w-full mt-2 mb-5 flex flex-row justify-between h-5">
          <span className="ml-5 text-lg">{t("import-csv")}</span>
          <img
            src={closeIcon}
            className="h-5 mr-3 cursor-pointer"
            onClick={() => {
              props.hideView();
            }}
          />
        </div>

        <div className="w-full mt-2 mb-5 flex flex-row justify-start gap-5 ">
          <div className="" />
          <CardDropdownInput
            _id="csv_input_area"
            translationKey="area"
            value={
              availableAreas.find((e) => e.idArea === inputIdArea)?.name ?? availableAreas[0]?.name
            }
            valueChangeHandler={(val) =>
              setInputIdArea(availableAreas.find((e) => e.name === val)?.idArea ?? 0)
            }
            optionValues={availableAreas.map((val) => val.name)}
          />

          <CardDropdownInput
            _id="layout_input"
            translationKey="layout"
            value={
              availableLayouts.find((e) => e.idArea === inputIdArea)?.name ??
              availableLayouts[0]?.name
            }
            valueChangeHandler={(val) => {
              setInputIdLayout(availableLayouts.find((e) => e.name === val)?.idLayout);
            }}
            optionValues={availableLayouts.map((val) => val.name)}
          />
        </div>

        <div className="w-full mt-2 mb-5 flex flex-row justify-start gap-5">
          <div className="" />
          <a
            id="templateCsv"
            className="bg-jura-grey-button h-8 pl-4 pr-4 rounded-md text-jura-grey-font flex flex-col justify-center"
            download={"template.csv"}
            href="/default_area_import.csv"
          >
            {t("download-csv")}
          </a>

          <button
            disabled={inputIdArea === undefined || inputIdLayout === undefined}
            id="importButtonJuraHQ"
            className="bg-jura-grey-button h-8 pl-4 pr-4 rounded-md text-jura-grey-font"
            onClick={() => {
              document.getElementById("theJURA_CsvSelector")?.click();
            }}
          >
            {t("upload")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const DeleteUserView = (props: { hideView: () => void; handleDelete: () => void }) => {
  const [t, i18n] = useTranslation();

  return (
    <div
      className={`absolute w-full h-full top-0 left-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      {/* the actual container for the content */}
      <div className={`w-30em h-fit bg-jura-beige flex flex-col justify-start items-center`}>
        <div className="w-full mt-2 mb-5 flex flex-row justify-between h-5">
          <span className="ml-5 text-lg">{t("delete-users-question")}</span>
          <img
            src={closeIcon}
            className="h-5 mr-3 cursor-pointer"
            onClick={() => {
              props.hideView();
            }}
          />
        </div>
        <div className="w-full mt-2 mb-5 flex flex-row justify-center gap-5 ">
          <button
            className="bg-green-300 h-8 pl-4 pr-4 rounded-md text-black"
            onClick={() => {
              props.handleDelete();
              props.hideView();
            }}
          >
            {t("yes")}
          </button>
          <button
            className="bg-red-300 h-8 pl-4 pr-4 rounded-md text-black"
            onClick={() => {
              props.hideView();
            }}
          >
            {t("no")}
          </button>
        </div>
      </div>
    </div>
  );
};

export const EditUserView = (props: {
  setEditUserView: (show: CardInfo | OperatorInfo | undefined) => void;
  selectedCard: CardInfo | undefined;
}) => {
  const outletInfo = useOutletContext<{ currentUser: OperatorInfo }>();
  const [t, i18n] = useTranslation();
  const [inputSurname, setInputSurname] = useState<string | undefined>(props.selectedCard?.surname);
  const [inputFirstName, setInputFirstName] = useState<string | undefined>(
    props.selectedCard?.firstName
  );
  const [inputPicture, setInputPicture] = useState<File | undefined>();
  const [inputJobDescription, setInputJobDescription] = useState<string | undefined>(
    props.selectedCard?.jobDescription
  );
  const [inputEmployeeNumber, setInputEmployeeNumber] = useState<string | undefined>(
    props.selectedCard?.employeeNumber
  );
  const [inputEmail, setInputEmail] = useState<string | undefined>(props.selectedCard?.email);
  const [inputTelephoneNumber, setInputTelephoneNumbmer] = useState<string | undefined>(
    props.selectedCard?.telephone
  );
  const [inputMobileNumber, setInputMobileNumber] = useState<string | undefined>(
    props.selectedCard?.mobile
  );
  const [inputWebsite, setInputWebsite] = useState<string | undefined>(props.selectedCard?.website);
  const [inputIdLayout, setInputIdLayout] = useState<number | undefined>(
    props.selectedCard?.idLayout
  );
  const [inputIdArea, setInputIdArea] = useState<number>(
    props.selectedCard?.idArea ?? outletInfo.currentUser.idArea
  );

  const [busy, setBusy] = useState(false);
  const [flickerState, setFlickerState] = useState(true); // workaround to reload the card component

  const [availableAreas, setAvailableAreas] = useState<AreaInfo[]>([]);
  const [availableLayouts, setAvailableLayouts] = useState<LayoutInfo[]>([]);

  useEffect(() => {
    if (outletInfo.currentUser.role === OperatorRole.ADMIN) {
      getAllAreaEntries().then((res) => {
        if (res) setAvailableAreas(res);
      });
    } else {
      getAreaEntry(outletInfo.currentUser.idArea).then((res) => {
        if (res) setAvailableAreas([res]);
      });
    }
  }, []);

  useEffect(() => {
    if (inputIdArea !== undefined) {
      // regardless of what has been set, set the available layouts
      getAllLayoutEntries(inputIdArea).then((res) => {
        setAvailableLayouts(res);

        if (inputIdArea !== props.selectedCard?.idArea) {
          // if the area has been changed from the original setting on an existing card, reset the layout entry
          setInputIdLayout(res[0].idLayout);
        } else if (inputIdArea === props.selectedCard?.idArea) {
          setInputIdLayout(props.selectedCard.idLayout); // use the existing value if switching back to original area
        }
      });
    }
  }, [inputIdArea]);

  const flickerFlickerState = (ms: number) => {
    setFlickerState(false);
    new Promise((resolve) => setTimeout(resolve, ms)).then(() => {
      setFlickerState(true);
    });
  };

  const handleSubmitEdit = async () => {
    if (busy) {
      console.error("was busy");
      return;
    }
    setBusy(true);

    const newInfo: CardInfo = {
      idArea: inputIdArea,
      email: inputEmail ?? "",
      employeeNumber: inputEmployeeNumber ?? "00",
      firstName: inputFirstName ?? "",
      jobDescription: inputJobDescription ?? "",
      idLayout: inputIdLayout ?? 0,
      nfcId: props.selectedCard?.nfcId ?? "",
      surname: inputSurname ?? "",
      telephone: inputTelephoneNumber ?? "",
      mobile: inputMobileNumber ?? "",
      website: inputWebsite ?? "jura.com",
    };

    if (!props.selectedCard) {
      const succCard = await createNewCard(newInfo);
      if (succCard && inputPicture) {
        const uploadSucc = await uploadImage(
          inputPicture,
          succCard.nfcId + `.${getFileTypeFromName(inputPicture.name)}`
        );
        if (uploadSucc) {
          // props.setEditUserView(undefined);
        } else {
          showErrorNotification(t("error-upload-image-title"));
        }
      }
      if (succCard) {
        props.setEditUserView(undefined);
        showSuccessNotification(t("success-created-new-card-title"));
      } else {
        showErrorNotification(t("error-create-card-title"));
      }
      setBusy(false); // either way set busy to false when done waiting
    } else if (props.selectedCard) {
      const succUpdate = await updateCardInfo(newInfo);
      if (succUpdate && inputPicture) {
        const uploadSucc = await uploadImage(
          inputPicture,
          props.selectedCard.nfcId + `.${getFileTypeFromName(inputPicture.name)}`
        );
        if (uploadSucc) {
          // props.setEditUserView(undefined);
        } else {
          showErrorNotification(t("error-upload-image-title"));
        }
      }
      if (succUpdate) {
        // props.setEditUserView(undefined);
        showSuccessNotification(t("success-updated-card-title"));
      } else {
        showErrorNotification(t("error-update-card-title"));
      }
      setBusy(false);
    }
  };

  return (
    <div
      id="edit_user_overlay"
      className={`absolute w-full h-full top-0 left-0 bg-dark-transparent flex flex-col justify-center items-center`}
    >
      <div
        className={`w-edit-user-box h-edit-user-box max-w-full max-h-full bg-jura-beige flex flex-col justify-center items-center`}
      >
        <div className="w-full flex flex-row justify-end h-5">
          <img
            src={closeIcon}
            className="h-5 mr-3 cursor-pointer"
            onClick={() => {
              if (!busy) {
                props.setEditUserView(undefined);
              } else {
                console.warn("was busy saving, didn't close the window");
              }
            }}
          />
        </div>
        <div id="editBoxInnerContainer" className="w-90percent h-97-5 ">
          <div className="w-full h-full overflow-auto ">
            {/* The split view, left the user details entry, right the preview */}
            <div className="w-full h-full min-w-edit-user-box flex flex-row justify-between items-center">
              {/* The edit user info part, (left side), put in an extra container to have utility buttons at the bottom */}
              <div className="w-2/3 h-full min-h-fit flex flex-col justify-between items-center">
                <div
                  id="editBoxEntry"
                  className="w-full flex flex-col justify-start items-center gap-4 "
                >
                  {/* The title of the window */}
                  <div className="w-full flex flex-row justify-start items-center text-3xl font-bold">
                    {t("employee-card")}
                  </div>
                  {/* The first row: name, first name and picture */}
                  <div className="w-full flex flex-row justify-start items-center gap-4">
                    <CardStringEntryInput
                      _id="surname_input"
                      translationKey="surname"
                      value={inputSurname ?? ""}
                      valueChangehandler={setInputSurname}
                    />
                    <CardStringEntryInput
                      _id="firstName_input"
                      translationKey="firstName"
                      value={inputFirstName ?? ""}
                      valueChangehandler={setInputFirstName}
                    />
                    <CardImageEntryInput
                      _id="surname_picture"
                      translationKey="picture"
                      valueChangehandler={(file) => {
                        setInputPicture(file);
                      }}
                      file={inputPicture}
                    />
                  </div>
                  {/* The second row: job description and employee number */}
                  <div className="w-full flex flex-row justify-start items-center gap-4">
                    <CardStringEntryInput
                      _id="job_input"
                      translationKey="jobDescription"
                      value={inputJobDescription ?? ""}
                      valueChangehandler={setInputJobDescription}
                    />
                    <CardStringEntryInput
                      _id="employeeNumber_input"
                      translationKey="employeeNumber"
                      value={inputEmployeeNumber?.toString() ?? ""}
                      valueChangehandler={(e) => {
                        setInputEmployeeNumber(e.replace(/[^\d.-]+/g, ""));
                        // setInputEmployeeNumber(isNaN(parseInt(e)) ? 0 : parseInt(e));
                      }}
                    />
                  </div>
                  {/* The third row: email and telephone */}
                  <div className="w-full flex flex-row justify-start items-center gap-4">
                    <CardStringEntryInput
                      _id="email_input"
                      translationKey="email"
                      value={inputEmail ?? ""}
                      valueChangehandler={setInputEmail}
                    />
                    <CardStringEntryInput
                      _id="telephone_input"
                      translationKey="telephone"
                      value={inputTelephoneNumber ?? ""}
                      valueChangehandler={setInputTelephoneNumbmer}
                    />
                  </div>
                  {/* The 4th row: area */}
                  <div className="w-full flex flex-row justify-start items-center gap-4">
                    <CardStringEntryInput
                      _id="mobile_input"
                      translationKey="mobile"
                      value={inputMobileNumber ?? ""}
                      valueChangehandler={setInputMobileNumber}
                    />

                    {availableAreas !== undefined && (
                      <CardDropdownInput
                        _id="area_input"
                        translationKey="area"
                        value={
                          availableAreas.find((e) => e.idArea === inputIdArea)?.name ??
                          availableAreas[0]?.name
                        }
                        valueChangeHandler={(val) =>
                          setInputIdArea(availableAreas.find((e) => e.name === val)?.idArea ?? 0)
                        }
                        optionValues={availableAreas.map((val) => val.name)}
                      />
                    )}
                  </div>
                  {/* The 5th row: address */}
                  <div className="w-full flex flex-row justify-start items-center gap-4">
                    {availableLayouts !== undefined && (
                      <CardDropdownInput
                        _id="layout_input"
                        translationKey="layout"
                        value={
                          availableLayouts.find((e) => e.idLayout === inputIdLayout)?.name ??
                          availableLayouts[0]?.name
                        }
                        valueChangeHandler={(val) => {
                          setInputIdLayout(availableLayouts.find((e) => e.name === val)?.idLayout);
                        }}
                        optionValues={availableLayouts.map((val) => val.name)}
                        // displayOptionValues={availableLayouts?.map((val) => val.name)}
                      />
                    )}
                  </div>
                </div>

                {/* utility Buttons (copy, save) */}
                <div className="w-full mt-3 flex flex-row justify-end items-center gap-3">
                  {props.selectedCard?.nfcId !== undefined && (
                    <button
                      className="bg-jura-darkgrey-button h-fit pl-4 pr-4 rounded-md text-white"
                      onClick={() => {
                        if (props.selectedCard?.nfcId)
                          copyToClipboard(
                            getNFCLink(props.selectedCard!.nfcId),
                            "info-copied-nfclink"
                          );
                      }}
                    >
                      {t("copy-nfc-link")}
                    </button>
                  )}
                  <button
                    className="bg-jura-darkgrey-button h-fit pl-4 pr-4 rounded-md text-white mr-8"
                    onClick={() => {
                      handleSubmitEdit().then(() => {
                        flickerFlickerState(50); // to reload the card preview
                      });
                    }}
                  >
                    {t("save")}
                  </button>
                </div>
              </div>

              {/* The preview part (right side) TODO: implement */}
              <div
                id="editBoxPreview"
                className={`bg-white w-1/3 h-full max-h-full overflow-scroll flex flex-row max-w justify-center font ${
                  !props.selectedCard ? "hidden" : ""
                }`}
                style={{ zoom: 0.7 }}
              >
                {flickerState && <Card nfcId={props.selectedCard?.nfcId} />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CardDropdownInput = (props: {
  _id: string;
  translationKey: string;
  value: string | undefined;
  optionValues: string[];
  valueChangeHandler: (value: string) => void;
}) => {
  const [t, i18n] = useTranslation();

  return (
    <div>
      <label htmlFor={`${props._id}_input`} className="block text-sm mb-1 font-semibold">
        {t(props.translationKey)}:
      </label>
      <select
        id={`${props._id}_input`}
        value={props.value}
        className={`rounded-md block p-1`}
        name={`_id_selects`}
        onChange={(e) => {
          props.valueChangeHandler(e.target.value);
        }}
      >
        {props.optionValues?.map((val, i) => {
          return (
            <option
              key={`${props._id}_option_${val}`}
              id={`${props._id}_option_${val}`}
              value={val}
            >
              {val}
            </option>
          );
        })}
      </select>
    </div>
  );
};

export const CardStringEntryInput = (props: {
  _id: string;
  translationKey: string;
  value: string | undefined;
  valueChangehandler: (text: string) => void;
}) => {
  const [t, i18n] = useTranslation();
  return (
    <div>
      <label htmlFor={props._id} className="block text-sm mb-1 font-semibold min-w-editUserLabel">
        {t(props.translationKey)}:
      </label>
      <input
        id={props._id}
        type="text"
        className={`rounded-md block p-1`}
        value={props.value}
        onChange={(ev) => {
          props.valueChangehandler(ev.target.value);
        }}
      />
    </div>
  );
};
export const CardImageEntryInput = (props: {
  _id: string;
  translationKey: string;
  valueChangehandler: (file: File | undefined) => void; // for setting the  actual file somewhere at the higher level
  file: File | undefined;
}) => {
  const [t, i18n] = useTranslation();

  return (
    <div>
      <label className="block text-sm mb-1 font-semibold min-w-editUserLabel">
        {t(props.translationKey)}:
        <label className="inline text-sm font-thin text-gray-600">
          {` (${t("img-requirements-hint")})`}
        </label>
      </label>

      <div className="flex flex-row w-fit justify-center gap-4">
        <img
          src={uploadIcon}
          className="cursor-pointer h-img-input ml-1"
          onClick={() => {
            document.getElementById(props._id)?.click();
          }}
        />
        {/* <img src={deleteIcon} className="cursor-pointer h-img-input ml-1" onClick={() => {}} /> */}

        {!props.file && <span>{t("no-image")}</span>}
        {props.file !== undefined && (
          <img src={URL.createObjectURL(props.file)} className="max-h-8" />
        )}
      </div>

      <input
        id={props._id}
        type="file"
        className="hidden"
        onChange={(ev) => {
          if ((ev.target.files?.length ?? 0) > 0) {
            const theFile = ev.target.files![0];
            if (theFile.size > 10 * Math.pow(10, 6)) {
              console.error("chosen image is too large, max 10 * 10^6 bytes (10 MB)"); // todo: show somehwere a notification
              showErrorNotification(t("error-image-large-title"));
              return;
            }
            props.valueChangehandler(theFile);
            console.log("set file", theFile);
          }
        }}
        accept=".jpg,.png"
      />
    </div>
  );
};
